import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, Link, stripSearchParams, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import * as valibot from 'valibot'
import { ArrowLeftIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { SuperAdminBoundary } from './-components/admin/super-admin-boundary'
import { SessionDetailPending, SessionDetailSkeleton } from './-components/admin/sessions-pending'
import { CopyButton, formatUtc, LINK_CLASS, StoreDownNotice } from './-components/admin/traces-shared'
import { formatDurationHuman } from './-components/admin/session-timeline'
import type { SessionDetail } from './-components/admin/session-player-math'
import {
  deviceLine,
  LiveBadge,
  PersonAvatar,
  personLabel,
  RelativeTime,
  SessionPlayer,
} from './-components/admin/session-player'

// One browser tab's session, on one time axis. `?at=` centres the server's search window on the moment the linking
// row knows; without it, the last 7 days.

export const Route = createFileRoute('/_app/admin/sessions_/$sessionId')({
  validateSearch: valibot.object({
    at: valibot.optional(valibot.string()),
    wide: valibot.fallback(valibot.optional(valibot.boolean(), false), false),
  }),
  search: { middlewares: [stripSearchParams({ wide: false })] },
  component: RouteComponent,
  pendingComponent: SessionDetailPending,
  staticData: { fullWidth: true },
})

function RouteComponent() {
  const { sessionId } = Route.useParams()
  return (
    <div className={SETTINGS_STACK}>
      <Link to="/admin/sessions" className={cn(LINK_CLASS, 'flex w-fit items-center gap-1 text-sm')}>
        <ArrowLeftIcon className="size-3.5" />
        Sessions
      </Link>
      <SuperAdminBoundary what="this session">
        <Suspense fallback={<SessionDetailSkeleton />}>
          <SessionBody sessionId={sessionId} />
        </Suspense>
      </SuperAdminBoundary>
    </div>
  )
}

function SessionBody(props: { sessionId: string }) {
  const context = useMatch({ from: '/_app/admin/sessions_/$sessionId/_timeline' }).context
  const deferred = useDeferredValue(context.sessionQueryOptions)
  const { data } = useSuspenseQuery(deferred)
  if (!data.ok) return <StoreDownNotice reason={data.reason} />
  if (data.events.length === 0) return <NotFound sessionId={props.sessionId} />
  return (
    <div
      className={cn('flex flex-col gap-6 transition-opacity', deferred !== context.sessionQueryOptions && 'opacity-60')}
    >
      <SessionHeader session={data} />
      {/* Keyed by session: the cursor and the filters belong to one session. */}
      <SessionPlayer key={data.sessionId} session={data} />
    </div>
  )
}

function NotFound(props: { sessionId: string }) {
  const search = Route.useSearch()
  const where = search.wide
    ? 'in the last 30 days'
    : search.at
      ? `within a day of ${formatUtc(search.at)}`
      : 'in the last 7 days'
  return (
    <div role="status" className="flex flex-col gap-1 rounded-lg border p-4">
      <span className="text-sm font-medium">Session not found</span>
      <span className="text-muted-foreground text-sm">
        No events {where} carry session <span className="font-mono">{props.sessionId.slice(0, 8)}</span>.{' '}
        {!search.wide && (
          <Link to="." search={{ wide: true }} className={LINK_CLASS}>
            Search the last 30 days
          </Link>
        )}
      </span>
    </div>
  )
}

function SessionHeader(props: { session: SessionDetail }) {
  const session = props.session
  const counts = session.counts
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <PersonAvatar person={session.user} size="lg" />
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-semibold tracking-tight">{personLabel(session.user)}</h2>
            {session.user?.email && <span className="text-muted-foreground text-sm">{session.user.email}</span>}
            {session.company && <span className="text-muted-foreground text-sm">· {session.company.name}</span>}
            {session.live ? <LiveBadge /> : <Badge variant="secondary">Ended</Badge>}
          </div>
          <span className="text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            {session.startedAt && (
              <span className="tabular-nums" title={formatUtc(session.startedAt)}>
                Started <RelativeTime iso={session.startedAt} />
              </span>
            )}
            <span aria-hidden>·</span>
            <span>{deviceLine(session.device)}</span>
            {[session.device.locale, session.device.timezone].filter(Boolean).map((value) => (
              <span key={value} className="contents">
                <span aria-hidden>·</span>
                <span>{value}</span>
              </span>
            ))}
            {session.device.appVersion && (
              <>
                <span aria-hidden>·</span>
                <span className="font-mono" title="Build">
                  {session.device.appVersion}
                </span>
              </>
            )}
            <span aria-hidden>·</span>
            <span className="flex items-center font-mono" title={session.sessionId}>
              {session.sessionId.slice(0, 8)}
              <CopyButton value={session.sessionId} label="Copy session id" />
            </span>
          </span>
          {session.truncated && (
            <span className="text-xs text-amber-700 dark:text-amber-400">
              Showing the first {session.events.length} events: the session goes on past them.
            </span>
          )}
        </div>
      </div>
      <dl className="flex items-center gap-5">
        <Stat label="Duration" value={formatDurationHuman(session.durationMs)} />
        <Stat label="Pages" value={counts.pages} />
        <Stat label="Actions" value={counts.interactions} />
        <Stat label="Requests" value={counts.requests} />
        {counts.rageClicks > 0 && <Stat label="Rage clicks" value={counts.rageClicks} tone="warn" />}
        {counts.deadClicks > 0 && <Stat label="Dead clicks" value={counts.deadClicks} tone="warn" />}
        <Stat label="Errors" value={counts.errors} tone={counts.errors > 0 ? 'error' : undefined} />
      </dl>
    </div>
  )
}

function Stat(props: { label: string; value: number | string; tone?: 'warn' | 'error' | undefined }) {
  return (
    <div className="flex flex-col-reverse items-end">
      <dt className="text-muted-foreground text-[11px] tracking-wide uppercase">{props.label}</dt>
      <dd
        className={cn(
          'text-lg leading-tight font-semibold tabular-nums',
          props.tone === 'error' && 'text-destructive',
          props.tone === 'warn' && 'text-amber-600 dark:text-amber-400',
        )}
      >
        {props.value}
      </dd>
    </div>
  )
}
